import { useEffect } from 'react'
import type { NextPage } from 'next'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { Seo } from '@/layout/Seo'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { getGenericStaticPaths } from '@/utils/nextUtils/staticPages'
import { writeToSessionStorage } from '@/utils/session-storage'
import { getBasePath } from '@/utils/sitemap/base'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { SubmitView } from '@/views/SubmitView'

interface SubmitPageProps {
  locale: string
}

const SubmitPage: NextPage<SubmitPageProps> = ({ locale }) => {
  const { t } = useTranslate('create')
  const url = `${getBasePath(locale)}${paths.submit.index}`
  const { query } = useSafeRouter()
  const title = t('angelCreateMetaTitle', 'Angel: Shape The Future of Entertainment With Your Film Or Show')
  const description = t(
    'angelCreateMetaDescription',
    'Submit your TV show or movie to be considered by the Angel Guild. Receive audience votes, feedback on your submission, and see if your project scores high enough to be considered for distribution by Angel.',
  )

  useEffect(() => {
    if (query.promoCode) {
      const promoCode = getItemOrFirstEntry(query.promoCode)
      if (promoCode) {
        writeToSessionStorage('torchSubmissionPromoCode', promoCode)
      }
    }
  }, [query.promoCode])

  return (
    <>
      <Seo
        canonical={url}
        description={description}
        openGraph={{ title, description, url }}
        path={paths.submit.index}
        title={title}
      />
      <SubmitView />
    </>
  )
}

export const getStaticPaths = getGenericStaticPaths

export const getStaticProps = getStaticPropsErrorHandler<SubmitPageProps>(
  { filePath: 'pages/[locale]/submit/index.tsx' },
  async ({ locale }) => {
    const translations = await loadTranslations(locale, ['common', 'create'])

    return {
      props: {
        locale,
        ...translations,
      },
      revalidate: Durations.ONE_DAY_IN_SECONDS,
    }
  },
)

export default SubmitPage
